import { useState } from 'react';
import { styled } from 'styled-components';

import { CartIcon } from '@/assets/icons';
import AdultConfirmModal from '@/components/common/AdultConfirmModal';
import Typography from '@/components/common/Typography';
import { PATH } from '@/constants/path';
import { ProductContent } from '@/features/Home/types/product';
import useCartItemMutation from '@/hooks/RQHooks/market/cart/useCartItemMutatino';
import { RQUserKey } from '@/hooks/RQHooks/user/RQUserKey';
import useUserInfoQuery from '@/hooks/RQHooks/user/useUserInfoQuery';
import { GA_EVENT, toGaItem } from '@/lib/gtag';
import { useFavoriteStore } from '@/store/useFavoriteStore';
import clientToken from '@/utils/clientToken';
import { requireLogin } from '@/utils/requireLogin';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import HeartButton from '../common/button/HeartButton';

const NewProductCard = ({
  id,
  marketProductName,
  thumbnailUrl,
  price,
  discountRate,
  requiresAdultVerification,
}: ProductContent) => {
  const [isAdultVerifyModalOpen, setIsAdultVerifyModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<'navigate' | 'cart' | null>(null);

  const { mutate: addCart } = useCartItemMutation();
  const queryClient = useQueryClient();
  const router = useRouter();
  const isLoggedIn = Boolean(clientToken.getAccessToken());
  const { data: userInfo } = useUserInfoQuery();
  const handleAddToCartSuccess = () => {
    GA_EVENT('add_to_cart', {
      currency: 'KRW',
      value: price,
      items: [
        toGaItem({
          productId: id,
          marketProductName,
          price,
          quantity: 1,
        }),
      ],
    });
  };

  const handleAdultVerifySuccess = () => {
    // 성인인증 완료 후 사용자 정보 재조회
    queryClient.invalidateQueries({ queryKey: RQUserKey.userInfo });

    // 대기 중인 액션 실행
    if (pendingAction === 'navigate') {
      router.push(`${PATH.PRODUCT}/${id}`);
    } else if (pendingAction === 'cart') {
      addCart(
        { productId: id, quantity: 1 },
        {
          onSuccess: handleAddToCartSuccess,
        }
      );
    }

    setPendingAction(null);
  };

  const handleAddCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (requireLogin(router)) return;

    // 성인인증이 필요한 상품인 경우에만 성인인증 체크
    if (requiresAdultVerification) {
      const isAdultVerified = userInfo?.isAdultVerification ?? false;

      if (!isAdultVerified) {
        setPendingAction('cart');
        setIsAdultVerifyModalOpen(true);
        return;
      }
    }

    addCart(
      { productId: id, quantity: 1 },
      {
        onSuccess: handleAddToCartSuccess,
      }
    );
  };

  const isFavorite = useFavoriteStore((state) => state.isFavoriteMarketProduct(Number(id)));

  const handleClick = () => {
    // 성인인증이 필요한 상품인 경우 성인인증 체크
    if (requiresAdultVerification) {
      const isAdultVerified = userInfo?.isAdultVerification ?? false;

      if (!isAdultVerified) {
        setPendingAction('navigate');
        setIsAdultVerifyModalOpen(true);
        return;
      }
    }

    router.push(`${PATH.PRODUCT}/${id}`);
    queryClient.setQueryData(
      RQUserKey['userRecentBannerSee'],
      //eslint-disable-next-line
      (oldData: any) => {
        // 기존 데이터가 없거나 초기 상태일 경우 대비
        if (!oldData || !oldData.result || !oldData.result.content) {
          return {
            ...oldData,
            result: {
              ...oldData?.result,
              content: [
                {
                  id,
                  marketProductName,
                  thumbnailUrl,
                  discountRate,
                  price,
                  originPrice: 0,
                  reviewRate: 0,
                  stockAvailable: 0,
                  label: '',
                },
              ],
            },
          };
        }

        // 중복 체크
        const isAlreadyExists = oldData.result.content.some(
          //eslint-disable-next-line
          (item: any) => item.id === id
        );
        if (isAlreadyExists) return oldData; // 중복이면 기존 데이터 그대로 반환

        // 새로운 상품을 추가하고 최대 3개로 제한
        const newContent = [
          {
            id,
            marketProductName,
            thumbnailUrl,
            discountRate,
            price,
            originPrice: 0,
            reviewRate: 0,
            stockAvailable: 0,
            label: '',
          },
          ...oldData.result.content,
        ].slice(0, 3); // 최대 3개까지만 유지

        return {
          ...oldData,
          result: {
            ...oldData.result,
            content: newContent,
          },
        };
      }
    );
  };

  return (
    <>
      <CardContainer onClick={handleClick}>
        <ImageWrapper>
          <ProductImage src={thumbnailUrl} alt={marketProductName} />
          {requiresAdultVerification && <AdultBadge>19</AdultBadge>}
          {/* 신상품 칸(FeaturedProductSection isNew) 전용 카드라 늘 단다. 서버 /new 가 이미 신규 기준일 3개월 이내만 준다.
              검색 키워드(tagList) 길이로 가르면 키워드 유무가 뱃지가 된다(#387). */}
          <Badge>
            <Typography variant="b2" color="white">
              신상품
            </Typography>
          </Badge>
          {isLoggedIn && (
            <HeartButton productId={Number(id)} isFavorite={isFavorite} productType="market" />
          )}
        </ImageWrapper>
        <AddToCartButton onClick={handleAddCart}>
          <CartIcon name="cart" width={16} />
          <Typography variant="h3">담기</Typography>
        </AddToCartButton>
        <CardContent>
          <Typography ellipsisLines={2} variant="b1" color="gray1000" align="center">
            {marketProductName}
          </Typography>

          <PriceInfo>
            {Boolean(discountRate) && (
              <Typography variant="h2" color="point2">
                {discountRate}%
              </Typography>
            )}
            <Typography variant="h2">{price.toLocaleString()}원</Typography>
          </PriceInfo>
        </CardContent>
      </CardContainer>
      <AdultConfirmModal
        isOpen={isAdultVerifyModalOpen}
        handleClose={() => setIsAdultVerifyModalOpen(false)}
        handleAfterSuccess={handleAdultVerifySuccess}
        type={pendingAction === 'navigate' ? 'navigate' : 'cart'}
        redirectUrl={pendingAction === 'navigate' ? `${PATH.PRODUCT}/${id}` : undefined}
      />
    </>
  );
};

export default NewProductCard;

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 232px; /* 기본 PC 크기 */
  overflow: hidden;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.gray50};

  @media (max-width: 768px) {
    max-width: 200px; /* 태블릿에서 조금 작게 */
  }

  @media (max-width: 480px) {
    max-width: 180px; /* 모바일에서는 더 작게 */
  }
  @media (max-width: 375px) {
    max-width: 130px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 /1; /* 가로 3 : 세로 4 비율 유지 */
  overflow: hidden;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.1s ease-in;
  &:hover {
    transform: scale(1.03);
  }
`;

const Badge = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  padding: 3px 7px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.point2};
`;

const CardContent = styled.div`
  margin-top: 13px;
  padding: 0 16px 16px;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 16px 0;
`;

const AddToCartButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 8px;
  margin-top: 13px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
`;

const AdultBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: white;
  border: 2px solid #e74c3c;
  color: #e74c3c;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;
